import React from 'react';
import { constructionServices, packages } from '../data/mock';
import { Button } from '../components/ui/button';
import { Building, Hammer, Sofa, Wrench, FileCheck, Check, ArrowRight, Shield, Home, Award, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

const iconMap = {
  Building,
  Hammer,
  Sofa,
  Wrench,
  FileCheck
};

const ConstructionPage = () => {
  const features = [
    { icon: Shield, title: 'High Quality', description: 'Top notch services and source materials' },
    { icon: Home, title: 'Customised Packages', description: 'Inclusive of all home decor services' },
    { icon: Award, title: 'Smart Tech Homes', description: 'Efficient and resilient smart homes' },
    { icon: Clock, title: 'On-Time Delivery', description: 'Competitive timelines that suit your schedule' }
  ];

  return (
    <div className="min-h-screen">

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&w=1200"
            alt="Construction"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <span className="text-brand-primary font-semibold text-sm uppercase tracking-wider">
              Home Construction
            </span>
            <h1 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              Best Home Construction Company in <span className="text-brand-primary">Bangalore</span>
            </h1>
            <p className="mt-6 text-xl text-gray-300">
              On Time. Within Budget. Quality Backed By Warranty.
            </p>
            <p className="mt-4 text-gray-400 leading-relaxed">
              We transform your dreams into reality with relentless commitment to quality and innovation. From contemporary apartments to luxurious villas, we ensure exquisite craftsmanship.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-brand-primary hover:bg-brand-dark text-white px-8">
                  Get Free Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              We Build, Maintain & <span className="text-brand-primary">Upgrade</span> Your Home
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {constructionServices.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <div
                  key={index}
                  className="bg-gray-50 rounded-2xl p-6 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-brand-light rounded-xl flex items-center justify-center mx-auto mb-4">
                    {IconComponent && <IconComponent className="w-7 h-7 text-brand-primary" />}
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-sm text-gray-600">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              What Makes Us <span className="text-brand-primary">Different?</span>
            </h2>
            <p className="mt-4 text-gray-600">Delivering unique experiences, one service at a time.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-7 h-7 text-brand-primary" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Construction Packages */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Construction <span className="text-brand-primary">Packages</span>
            </h2>
            <p className="mt-4 text-gray-600">Make your home the best with InstaSpaces design, quality and expertise</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {packages.construction.map((pkg) => (
              <div
                key={pkg.id}
                className={`relative bg-white rounded-3xl border-2 overflow-hidden transition-all duration-300 hover:shadow-xl ${pkg.popular ? 'border-brand-primary' : 'border-gray-200'
                  }`}
              >
                {pkg.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-brand-primary text-white text-center py-2 text-sm font-medium">
                    Recommended
                  </div>
                )}

                <div className={`p-8 ${pkg.popular ? 'pt-14' : ''}`}>
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900">{pkg.name}</h3>
                    <div className="flex items-baseline justify-center mt-3">
                      <span className="text-lg">₹</span>
                      <span className="text-4xl font-bold text-brand-primary">{pkg.price}</span>
                      <span className="text-gray-500">{pkg.unit}</span>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link to="/contact">
                    <Button className="w-full bg-brand-primary hover:bg-brand-dark text-white">
                      Get in Touch
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold">
              Superior Technology for <span className="text-brand-primary">Superior Finishes</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'DuraBuild™ Cabinets', description: 'Real heroes offering unparalleled strength and durability' },
              { title: 'AntiBubble® Technology', description: 'Materials happily stuck together for life' },
              { title: 'AquaBloc® Technology', description: 'Edge banding with thermoplastic glue for water resistance' }
            ].map((tech, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <h3 className="text-xl font-bold mb-3">{tech.title}</h3>
                <p className="text-gray-400">{tech.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ConstructionPage;
